import React from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import visuallyHidden from "theme/visually-hidden";

const FrontIconWrapper = styled.div`
  align-items: flex-start;
  display: flex;
  position: absolute;
  height: 29px;
  width: 24px;
`;

const SelectLabel = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.fontSize(2)};
  color: ${({ theme }) => theme.themeColors.secondary};
  line-height: 18px;
  position: absolute;
  top: 12px;
  transition: all 0.2s;
  font-weight: 400;

  ${({ showRequiredAsterisk, theme }) =>
    showRequiredAsterisk &&
    css`
      :after {
        content: "*";
        color: ${theme.themeColors.primary};
        font-size: 1rem;
        font-weight: 400;
        line-height: 0.3125rem;
        position: absolute;
        right: -10px;
        top: 50%;
      }
    `}

  ${({ hideLabelForPlaceholder }) =>
    hideLabelForPlaceholder &&
    css`
      ${visuallyHidden};
    `}

  ${({ isActive }) =>
    isActive &&
    css`
      font-size: ${({ theme }) => theme.fontSize(1)};
      transform: translate(0, -25px);
    `}
`;

const InputWrapper = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.themeColors.secondary};
  border-radius: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 0;
  height: 42px;
  padding: 0 10px 0 0;
  position: relative;
  width: 100%;

  :hover {
    border-bottom: 1px solid ${({ theme }) => theme.themeColors.secondary};
  }

  :focus-within {
    border-bottom: 1px solid ${({ theme }) => theme.themeColors.primary};
  }
  ${({ error }) =>
    error &&
    css`
      border-color: ${({ theme }) => theme.themeColors.danger};
    `}

  ${({ success }) =>
    success &&
    css`
      border-color: ${({ theme }) => theme.themeColors.success};
    `}

  .pac-target-input {
    &::placeholder {
      color: ${({ theme }) => theme.themeColors.secondary};
      font-size: ${({ theme }) => theme.fontSize(2)};
      margin-bottom: 20px;
    }
  }

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.5;
    `}
`;

function AddressDropdownContent({
  className,
  disabled,
  id,
  input: { value },
  isSearch,
  label,
  placeholder,
  LeadingIcon,
  meta: { active, success } = {},
  showRequiredAsterisk,
}) {
  return (
    <div>
      <InputWrapper
        className={className}
        disabled={disabled}
        isSearch={isSearch}
        success={success}
      >
        <SelectLabel
          hideLabelForPlaceholder={!!placeholder}
          isActive={active || value}
          htmlFor={id}
          showRequiredAsterisk={showRequiredAsterisk}
        >
          {label}
        </SelectLabel>

        {LeadingIcon && <FrontIconWrapper>{LeadingIcon}</FrontIconWrapper>}
      </InputWrapper>
    </div>
  );
}

AddressDropdownContent.propTypes = {
  className: PropTypes.string,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  input: PropTypes.shape({
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    name: PropTypes.string,
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.shape({}),
    ]),
  }),
  isSearch: PropTypes.bool,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  LeadingIcon: PropTypes.node,
  meta: PropTypes.shape({
    error: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.object,
      PropTypes.string,
    ]),
    submitError: PropTypes.bool,
    success: PropTypes.bool,
  }),
  showRequiredAsterisk: PropTypes.bool,
};

AddressDropdownContent.defaultProps = {
  className: "",
  disabled: false,
  id: undefined,
  input: {
    onBlur: () => {},
    onChange: () => {},
    onFocus: () => {},
    name: undefined,
    value: "",
  },
  isSearch: false,
  label: null,
  placeholder: "",
  LeadingIcon: () => {},
  meta: { error: false, success: false, submitError: false },
  showRequiredAsterisk: false,
};

const AddressDropdown = (props) => {
  return <AddressDropdownContent {...props} />;
};

export default AddressDropdown;
